import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { MdCheckCircle, MdErrorOutline } from 'react-icons/md';
import { FormInput } from '@podus/ui';
import AuthLayout from '@/components/AuthLayout';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

interface VerifyResponse {
    valid: boolean;
    state: string;
    message?: string;
    data?: { name: string | null; maskedEmail: string; expiresAt: string };
}

interface AcceptResponse {
    token: string;
    data: { id: number; name: string; email: string; code: string; referralLink: string };
}

interface AvailabilityResponse {
    available: boolean;
    code: string;
    message: string;
}

/** Invitation acceptance — sets name, password and (optionally) a preferred code (§3.1). */
export default function AcceptInvite() {
    const [params] = useSearchParams();
    const token = params.get('token') ?? '';
    const navigate = useNavigate();
    const { login } = useAuth();

    const [checking, setChecking] = useState(true);
    const [invite, setInvite] = useState<VerifyResponse | null>(null);

    const [name, setName] = useState('');
    const [password, setPassword] = useState('');
    const [passwordConfirm, setPasswordConfirm] = useState('');
    const [preferredCode, setPreferredCode] = useState('');
    const [availability, setAvailability] = useState<AvailabilityResponse | null>(null);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [requested, setRequested] = useState(false);

    useEffect(() => {
        if (!token) {
            setChecking(false);
            return;
        }
        api.get<VerifyResponse>(`/ambassador/invite/${encodeURIComponent(token)}`)
            .then((res) => {
                setInvite(res.response);
                if (res.response.data?.name) setName(res.response.data.name);
            })
            .catch(() => setInvite({ valid: false, state: 'not_found', message: 'This invitation link is invalid.' }))
            .finally(() => setChecking(false));
    }, [token]);

    /* ── Live code availability (FR-20), debounced ── */
    const debounceRef = useRef<number | undefined>(undefined);
    const checkAvailability = useCallback((value: string) => {
        window.clearTimeout(debounceRef.current);
        if (!value.trim()) {
            setAvailability(null);
            return;
        }
        debounceRef.current = window.setTimeout(() => {
            api.get<AvailabilityResponse>(`/ambassador/codes/availability?code=${encodeURIComponent(value.trim())}`)
                .then((res) => setAvailability(res.response))
                .catch(() => setAvailability(null));
        }, 350);
    }, []);

    useEffect(() => () => window.clearTimeout(debounceRef.current), []);

    const handleRequestNew = async () => {
        try {
            await api.post('/ambassador/invite/request-new', { token });
        } catch {
            // The endpoint is intentionally non-committal; treat any outcome the same.
        }
        setRequested(true);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (password !== passwordConfirm) {
            setError('Passwords do not match');
            return;
        }
        if (preferredCode.trim() && availability && !availability.available) {
            setError(availability.message);
            return;
        }

        setBusy(true);
        try {
            const res = await api.post<AcceptResponse>('/ambassador/accept-invite', {
                token,
                name: name.trim(),
                password,
                passwordConfirm,
                ...(preferredCode.trim() ? { preferredCode: preferredCode.trim().toLowerCase() } : {}),
            });

            const { token: authToken, data } = res.response;
            login(authToken, {
                id: data.id,
                name: data.name,
                email: data.email,
                role: 'ambassador',
                code: data.code,
                referralLink: data.referralLink,
            });
            navigate('/dashboard', { replace: true });
        } catch (err) {
            setError(err instanceof ApiError ? err.message : 'Could not activate your account. Please try again.');
        } finally {
            setBusy(false);
        }
    };

    if (checking) {
        return (
            <AuthLayout title="Checking your invitation…">
                <div className="skeleton-shimmer h-40 w-full rounded-xl" />
            </AuthLayout>
        );
    }

    if (!token || !invite?.valid) {
        const expired = invite?.state === 'expired';
        return (
            <AuthLayout
                title={expired ? 'This invitation has expired' : 'This invitation is no longer valid'}
                subtitle={invite?.message ?? 'The link you followed is invalid or has already been used.'}
                footer={
                    <>
                        Already have an account?{' '}
                        <Link to="/sign-in" className="font-bold text-primary">
                            Sign in
                        </Link>
                    </>
                }
            >
                <div className="rounded-xl border border-[#E0E0E0] bg-[#fafafa] p-6">
                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-amber-100 text-amber-600">
                        <MdErrorOutline size={22} />
                    </span>
                    <p className="mt-4 text-[14px] leading-6 text-[#747474]">
                        {expired
                            ? 'Invitations are single-use and expire for security. Ask us for a fresh one and an admin will send it over.'
                            : 'If you think this is a mistake, contact the admin who invited you.'}
                    </p>

                    {expired &&
                        (requested ? (
                            <p className="mt-5 flex items-center gap-2 text-[14px] font-semibold text-green-700">
                                <MdCheckCircle size={18} />
                                Request sent — an admin has been notified.
                            </p>
                        ) : (
                            <button
                                type="button"
                                onClick={handleRequestNew}
                                className="mt-5 h-11 w-full rounded-[10px] bg-secondary text-[14px] font-bold text-white transition-opacity hover:opacity-90"
                            >
                                Request a new invitation
                            </button>
                        ))}
                </div>
            </AuthLayout>
        );
    }

    const codeState = preferredCode.trim() ? availability : null;

    return (
        <AuthLayout
            title="Set up your ambassador account"
            subtitle={`Invitation for ${invite.data?.maskedEmail ?? 'your email address'}`}
        >
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                {error && (
                    <div
                        role="alert"
                        className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700"
                    >
                        {error}
                    </div>
                )}

                <FormInput
                    id="invite-name"
                    name="name"
                    title="Full name"
                    placeholder="Ada Obi"
                    required
                    variant="white"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                />

                <FormInput
                    id="invite-password"
                    name="password"
                    type="password"
                    title="Password"
                    placeholder="At least 8 characters"
                    autoComplete="new-password"
                    required
                    variant="white"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                />

                <FormInput
                    id="invite-password-confirm"
                    name="passwordConfirm"
                    type="password"
                    title="Confirm password"
                    placeholder="Repeat your password"
                    autoComplete="new-password"
                    required
                    variant="white"
                    value={passwordConfirm}
                    onChange={(e) => setPasswordConfirm(e.target.value)}
                    error={passwordConfirm && password !== passwordConfirm ? 'Passwords do not match' : undefined}
                />

                <div>
                    <FormInput
                        id="invite-code"
                        name="preferredCode"
                        title="Preferred referral code (optional)"
                        placeholder="adaobi"
                        variant="white"
                        value={preferredCode}
                        onChange={(e) => {
                            const value = e.target.value.toLowerCase();
                            setPreferredCode(value);
                            checkAvailability(value);
                        }}
                        error={codeState && !codeState.available ? codeState.message : undefined}
                    />
                    {codeState?.available && (
                        <p className="mt-1 flex items-center gap-1 text-[11px] font-medium text-green-600">
                            <MdCheckCircle size={13} />
                            {codeState.message}
                        </p>
                    )}
                    <p className="mt-1.5 text-[12px] leading-5 text-[#9a9a9a]">
                        3–30 characters, starting with a letter. Leave blank and we&rsquo;ll generate one for you.
                    </p>
                </div>

                <button
                    type="submit"
                    disabled={busy || !name.trim() || !password}
                    className="mt-2 h-12 w-full rounded-[10px] bg-secondary text-[14px] font-bold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                >
                    {busy ? 'Activating…' : 'Activate my account'}
                </button>
            </form>
        </AuthLayout>
    );
}
